import React, { useEffect, useRef, useState } from 'react';

import { faClose, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import { AGGREGATE_OPTIONS, AggregateFunctionType, AggregateItem } from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { Button, SelectFormInput } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';
import { cn } from 'utils/global';

type Props = {
  control: any;
  selectedColumnsData?: ReportingColumn[];
  onClose: VoidFunction;
  onSubmit: (aggregates: { func: AggregateFunctionType; column?: string }[]) => void;
  initialAggregates?: { func: AggregateFunctionType; column?: string }[];
};

const CreateReportTemplateAggregateModal: React.FC<Props> = ({
  control,
  selectedColumnsData = [],
  onClose,
  onSubmit,
  initialAggregates = [],
}) => {
  const { t } = useTranslation();
  const idCounter = useRef(0);
  const getNextId = () => {
    idCounter.current += 1;
    return idCounter.current;
  };
  const [aggregates, setAggregates] = useState<AggregateItem[]>([{ id: getNextId(), func: null, column: null }]);

  const getFilteredColumns = (aggFunc: AggregateFunctionType | null) => {
    return selectedColumnsData
      .filter((col) => {
        if (aggFunc === 'Distinct count of') {
          return true;
        }
        if (aggFunc && aggFunc !== 'Count') {
          return col.columnType === 3;
        }
        return false;
      })
      .map((col) => ({ label: col.name, value: col.name }));
  };

  const addAggregate = () => {
    setAggregates((prev) => [...prev, { id: getNextId(), func: null, column: null }]);
  };

  const removeAggregate = (id: number) => {
    setAggregates((prev) =>
      prev.length > 1
        ? prev.filter((aggregate) => aggregate.id !== id)
        : [{ id: getNextId(), func: null, column: null }]
    );
  };

  const updateAggregate = (id: number, key: 'func' | 'column', value: string | null) => {
    setAggregates((prev) =>
      prev.map((aggregate) => (aggregate.id === id ? { ...aggregate, [key]: value } : aggregate))
    );
  };

  const isSaveDisabled = !aggregates.some(
    (aggregate) => aggregate.func && (aggregate.func === 'Count' || aggregate.column)
  );

  useEffect(() => {
    if (initialAggregates.length > 0) {
      setAggregates(
        initialAggregates.map((aggregate) => ({
          id: getNextId(),
          func: aggregate.func,
          column: aggregate.column ?? null,
        }))
      );
    } else {
      setAggregates([{ id: getNextId(), func: null, column: null }]);
    }
  }, [initialAggregates]);

  return (
    <div className="pt-4 mb-2 flex flex-col gap-2">
      {aggregates.map((aggregate) => {
        const isColumnRequired = aggregate.func && aggregate.func !== 'Count';

        return (
          <div key={aggregate.id} className="flex items-center gap-2">
            <SelectFormInput
              control={control}
              name={`aggregateFunction-${aggregate.id}`}
              inputClassName={cn('w-[31.5rem]', {
                'w-[15.5rem]': isColumnRequired,
              })}
              placeholder={t('SelectAggregateFunction')}
              options={AGGREGATE_OPTIONS.map((val) => ({ label: val, value: val }))}
              defaultValue={aggregate.func ? { label: aggregate.func, value: aggregate.func } : undefined}
              onSelectValue={(opt) => {
                updateAggregate(aggregate.id, 'func', opt?.value || null);
                updateAggregate(aggregate.id, 'column', null);
              }}
            />

            {isColumnRequired && (
              <SelectFormInput
                control={control}
                name={`aggregateColumn-${aggregate.id}`}
                inputClassName="w-[15.5rem]"
                placeholder={t('SelectColumn')}
                options={getFilteredColumns(aggregate.func)}
                defaultValue={
                  aggregate.column
                    ? getFilteredColumns(aggregate.func).find((o) => o.value === aggregate.column) ?? undefined
                    : undefined
                }
                onSelectValue={(opt) => updateAggregate(aggregate.id, 'column', opt?.value || null)}
              />
            )}

            <FontAwesomeIcon
              icon={faClose}
              onClick={() => removeAggregate(aggregate.id)}
              className="text-neutral-500 w-4 h-4 cursor-pointer"
            />
          </div>
        );
      })}

      <Button
        className="w-[9rem]"
        type="button"
        onClick={addAggregate}
        variant={ButtonVariants.OUTLINE}
        size={ButtonSizeVariants.SM}
        disabled={isSaveDisabled}
      >
        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
        {t('AddAggregate')}
      </Button>

      <ModalControlButtons
        onClose={onClose}
        onSubmit={() =>
          onSubmit(
            aggregates
              .filter((aggregate) => aggregate.func && (aggregate.func === 'Count' || aggregate.column))
              .map((aggregate) => ({
                func: aggregate.func!,
                column: aggregate.column || undefined,
              }))
          )
        }
        saveButtonText={t('Save')}
        disabled={isSaveDisabled}
      />
    </div>
  );
};

export default CreateReportTemplateAggregateModal;
